import { ExternalLinkIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import type { InAppBrowserApp } from '@/lib/posthog/inAppBrowser';
import { GoogleLogo } from '@/modules/auth/components/GoogleLogo';
import {
  buildHandoffUrl,
  isHandoffFailed,
  type MobileOs,
} from '@/modules/auth/inAppHandoff';
import { authButtonClickedEvents } from '@/modules/auth/types/externalLogin';
import { Button } from '@/modules/ui/components/button/Button';

import { InAppBrowserSheet } from './inAppBrowserSheet/InAppBrowserSheet';

/** How long Android gets to hide the page by opening the browser. */
const ANDROID_HANDOFF_MS = 1500;

type FallbackReason = 'cannotDetect' | 'stillVisible' | 'fallbackLoaded';

const assignLocation = (url: string) => {
  window.location.href = url;
};

interface InAppGoogleSignUpProps {
  app: InAppBrowserApp;
  os: MobileOs;
  /** The page's query string; stories set it to open as Android's fallback page does. */
  search?: string;
  /** Opens the hand-off URL; stories replace it so the page stays put. */
  navigate?: (url: string) => void;
}

/**
 * Google's button for `/sign-up` inside an app's built-in browser, where
 * Google blocks sign-in. The tap reopens the page in the real browser, where
 * the Google button works; the sheet covers when that can't happen.
 * iOS can't tell whether Safari opened, so it shows the sheet every time.
 */
export const InAppGoogleSignUp = ({
  app,
  os,
  search = window.location.search,
  navigate = assignLocation,
}: InAppGoogleSignUpProps) => {
  const posthog = usePostHog();
  const trackEvent = useTrackEvent();
  // Why the sheet is open, or null when closed. Android's intent fallback
  // reloads the page here, marked, when no browser took it (`buildHandoffUrl`).
  const [sheetReason, setSheetReason] = useState<FallbackReason | null>(() =>
    isHandoffFailed(search) ? 'fallbackLoaded' : null,
  );
  // Stops the Android check of the latest attempt (timer and listener).
  const cancelAndroidCheck = useRef<(() => void) | null>(null);

  const reportFallbackShown = useEffectEvent((reason: FallbackReason) =>
    trackEvent('escapeFallbackShown', { os, reason }),
  );
  // Only a closed to open change reports: `showSheet` keeps the first reason
  // while the sheet is open, so Try again and late timers don't count again.
  useEffect(() => {
    if (sheetReason) reportFallbackShown(sheetReason);
  }, [sheetReason]);

  useEffect(() => () => cancelAndroidCheck.current?.(), []);

  const showSheet = (reason: FallbackReason) =>
    setSheetReason((current) => current ?? reason);

  const handOff = (retry: boolean) => {
    if (os === 'other') {
      showSheet('cannotDetect');
      return;
    }

    trackEvent('escapeAttempted', { os, retry });
    cancelAndroidCheck.current?.();

    // Opening the browser hides this page; still visible means it didn't.
    // The listener goes up first so a fast hide isn't missed.
    if (os === 'android') {
      let didLeave = false;
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') didLeave = true;
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      const cancel = () => {
        window.clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        cancelAndroidCheck.current = null;
      };
      const timer = window.setTimeout(() => {
        cancel();
        if (!didLeave) showSheet('stillVisible');
      }, ANDROID_HANDOFF_MS);
      cancelAndroidCheck.current = cancel;
    }

    navigate(
      buildHandoffUrl(window.location.href, os, posthog.get_distinct_id()),
    );

    if (os === 'ios') showSheet('cannotDetect');
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          trackEvent(authButtonClickedEvents.signUp, { authMethod: 'Google' });
          handOff(false);
        }}
        // Google's own button border and hover, so it reads as Google's.
        className="h-12 w-full gap-3 rounded-lg border-[#dadce0] px-3.5 shadow-none hover:bg-[#f8f9fa] hover:text-inherit active:scale-[0.98] has-[>svg]:px-3.5"
      >
        <GoogleLogo />
        <span className="flex flex-1 flex-col items-center leading-tight">
          <span className="text-foreground text-[15px] font-medium">
            Sign up with Google
          </span>
          <span className="text-muted-foreground text-xs">
            {os === 'ios' ? 'Opens in Safari' : 'Opens in your browser'}
          </span>
        </span>
        <ExternalLinkIcon
          className="text-muted-foreground size-[18px]"
          aria-hidden
        />
      </Button>
      <InAppBrowserSheet
        open={sheetReason !== null}
        onOpenChange={(open) => {
          if (!open) setSheetReason(null);
        }}
        app={app}
        os={os}
        onRetry={() => handOff(true)}
      />
    </>
  );
};
